import React, {ComponentType, useEffect} from 'react';

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
  skipToken,
} from '@reduxjs/toolkit/dist/query';
import {QuerySubState} from '@reduxjs/toolkit/dist/query/core/apiState';
import moment from 'moment-tz';
import {InjectedRouter, withRouter, WithRouterProps} from 'react-router';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {dataRangeTemplatesMap} from 'app/general/dates/date-range';
import {
  BlankSidebarPage,
  UseQueryType,
  VersionParams,
} from 'app/general/structures/BlankSidebarPage';
import {useReportRunDetail, useRunReport} from 'app/reports/api';
import {
  DocTaskReportItemType,
  RenderDocumentInstance,
} from 'app/reports/components/renderers/render-document-taskinstance';
import {getReportRunIDFromURL} from 'app/reports/utils';
import {getTaskReportURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {
  DocumentContentType,
  ListResponseType,
  ProcedureHashidSerializedType,
  ProcedureVersionSerializedType,
  ProcessHashidSerializedType,
  ProcessVersionSerializedType,
  ReportResultSerializedType,
} from 'sweet-types';

type ItemType = ProcedureHashidSerializedType | ProcessHashidSerializedType;

type ItemVersionType =
  | ProcedureVersionSerializedType
  | ProcessVersionSerializedType;

interface Props
  extends WithRouterProps<{
    hashid: string;
    reportid: string;
    versionid: null | string;
  }> {
  route?: {
    props: Record<string, unknown>;
  };
  contentType: DocumentContentType;
  useData: UseQueryType<string, ItemType>;
  useDataVersion: UseQueryType<
    string | number | {versionId: string | number},
    ItemVersionType
  >;
  useDataVersions: UseQueryType<
    VersionParams,
    ListResponseType<ItemVersionType>
  >;
  SideBarComponent: ComponentType<
    {
      versionId: string | null;
      version: ItemVersionType | undefined;
      userId: number;
      versions: Array<ItemVersionType> | undefined;
      route?: {
        props: Record<string, unknown>;
      };
      router: InjectedRouter;
    } & (
      | {procedure: ProcedureHashidSerializedType}
      | {process: ProcessHashidSerializedType}
    )
  >;
}

type UseQueryStateType = QuerySubState<
  QueryDefinition<
    string,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    never,
    ReportResultSerializedType<DocTaskReportItemType>,
    'sweetAPI'
  >
> & {isLoading: boolean};

export function BaseDocumentTaskReportPage({
  params,
  route,
  router,
  contentType,
  SideBarComponent,
  useData,
  useDataVersion,
  useDataVersions,
}: Props) {
  const {data: document} = useData(params.hashid);
  const {data: reportDetail, isLoading: isReportDetailLoading} =
    useReportRunDetail<UseQueryStateType>(
      params.reportid ? params.reportid : skipToken
    );
  const [runReport, {data: reportRun, isLoading: isReportRunLoading}] =
    useRunReport();
  const userTz = useCurrentUserState().data!.timezone;

  const isLoading = isReportRunLoading || isReportDetailLoading;

  useEffect(() => {
    if (!params.reportid && document) {
      // When no report is available; we load a default +7, -30 days interval report.
      const dateRange =
        dataRangeTemplatesMap['next-7-last-30-days'].build(userTz);

      runReport({
        starts_at: dateRange.start,
        ends_at: dateRange.end,
        slug: 'document-taskinstance',
        document_url: document.url,
      });
    }
  }, [document]);

  useEffect(() => {
    if (reportRun?.result?.url != undefined && document) {
      const reportRunID = getReportRunIDFromURL(reportRun.result.url);
      router.push(getTaskReportURL(document.html_url, reportRunID));
    }
  }, [reportRun]);

  const formattedParams =
    reportDetail != null
      ? `${moment(reportDetail.params.starts_at).format('ll')} - ${moment(
          reportDetail.params.ends_at
        ).format('ll')}`
      : null;

  return (
    <BlankSidebarPage
      contentType={contentType}
      useData={useData}
      useDataVersion={useDataVersion}
      useDataVersions={useDataVersions}
      SideBarComponent={SideBarComponent}
      route={route}
      router={router}
      params={params}
    >
      {isLoading && (
        <NoResultsMessage
          icon="fa-circle-notch fa-spin"
          head={gt('Generating report...')}
        >
          <p>{gt('Just a moment, your data is on its way...')}</p>
        </NoResultsMessage>
      )}
      {reportDetail != null ? (
        <>
          <div className="d-flex">
            <div className="col-12 col-sm">
              <p className="lead mt-3">{formattedParams}</p>
              <p className="text-muted">
                {gt(
                  'This report covers the next 7 days and last 30 days. For reports covering other date ranges,'
                )}{' '}
                <a
                  href="/accounts/reports/document-taskinstance/"
                  target="_blank"
                >
                  {gt('visit the report page.')}
                </a>
              </p>
            </div>
            <div className="col-auto d-flex align-items-center py-3 py-sm-0">
              <a
                href={`${reportDetail.url}export/`}
                className="btn btn-primary px-3"
              >
                {gt('Download')}
              </a>
            </div>
          </div>
          <RenderDocumentInstance result={reportDetail} />
        </>
      ) : null}
    </BlankSidebarPage>
  );
}

export const DocumentTaskReportPage = withRouter(BaseDocumentTaskReportPage);
